import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Box, Fab, Stack, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import Axios from '../../Assets/Axios/Axios';

import ToastContext from '../../Context/ToastContext';
import { useAuthContext } from '../../Context/AuthContext';

import Layout from './Components/Layout';
import ReviewManageCard from './Components/ReviewManageCard';

import { FindUserUnReviewedOrders } from '../../Server/Query/index';

const ReviewManage = () => {
  const { user } = useAuthContext();
  const { showToastMessage } = useContext(ToastContext);

  const [ordersToReview, setOrdersToReview] = useState([]);
  const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);

  const fetchReviews = useCallback(() => {
    if (!user?.userID) return undefined;
    const query = FindUserUnReviewedOrders({ userId: user.userID });
    Axios(query)
      .then(({ errors, data }) => {
        if (errors) {
          showToastMessage('Unable to load data. Please refresh the page and try again.');
          return undefined;
        }

        setOrdersToReview((data?.UnReviewedOrders ?? []).map(parseOrder));
      })
      .catch(() => showToastMessage('Unable to load data. Please refresh the page and try again.'));
  }, [showToastMessage, user?.userID]);

  useEffect(fetchReviews, [fetchReviews]);

  function handleScrollToTop(event) {
    event.currentTarget.parentElement?.scroll({ top: 0, behavior: 'smooth' });
  }

  function handleScrollTopVisibility(event) {
    const shouldScrollTopVisible = event.currentTarget.scrollTop > 100;
    if (shouldScrollTopVisible !== isScrollTopVisible)
      setIsScrollTopVisible(shouldScrollTopVisible);
  }

  return (
    <Layout>
      <Stack
        alignItems={{ xs: 'center', md: 'unset' }}
        justifyContent={{ xs: 'center', md: 'unset' }}
        flexGrow='1'
      >
        {ordersToReview.length ? (
          <div style={{ position: 'relative', width: 'min(100%,650px)' }}>
            <Box
              boxSizing='border-box'
              display='grid'
              maxHeight='500px'
              overflow='auto'
              gap={2}
              px={2}
              pb={4}
              onScroll={handleScrollTopVisibility}
            >
              {ordersToReview.map(order => (
                <ReviewManageCard
                  key={order.orderId}
                  updateParentReviews={fetchReviews}
                  {...order}
                />
              ))}
              {isScrollTopVisible && (
                <Fab
                  size='small'
                  sx={{ position: 'absolute', right: '16px', bottom: '8px' }}
                  onClick={handleScrollToTop}
                >
                  <ArrowUpwardIcon />
                </Fab>
              )}
            </Box>
          </div>
        ) : (
          <Typography
            fontWeight={500}
            width='100%'
            textAlign='center'
            variant='h6'
            color='text.secondary'
          >
            There are no orders to review
          </Typography>
        )}
      </Stack>
    </Layout>
  );
};

function parseOrder(order) {
  const professional = order.ServicePackage?.Service?.Professional ?? {};

  return {
    orderId: order.id,
    serviceId: order.serviceId,
    servicePackageId: order.servicePackageId,
    professionalName:
      [professional.first_name, professional.last_name].filter(Boolean).join(' ') || 'Professional',
    referenceId: order.reference_id ?? order.referenceId ?? order.orderRef,
    date: order.date ? order.date.split('T')[0] : '',
  };
}

export default ReviewManage;
